import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import {
  Route as RouteIcon,
  GetApp as DownloadIcon,
  DateRange as DateIcon,
  Store as StoreIcon,
  FilterList as FilterIcon,
  PlayArrow as GenerateIcon,
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';
import { cacheEndpoints } from '../../utils/apiCache';

const REPORT_TIMEOUT_MS = 10 * 60 * 1000;

const todayStr = () => new Date().toISOString().split('T')[0];
const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const RiderRouteDistanceReport = () => {
  const { actions } = useApp();

  const [filters, setFilters] = useState({
    from: daysAgoStr(15),
    to: todayStr(),
    storeId: 'all',
    riderIds: [],
    includeReturns: true,
    sequence: 'planned',
  });

  const [stores, setStores] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const init = async () => {
      try {
        const storesData = await cacheEndpoints.stores.getAll();
        setStores(storesData || []);
      } catch (e) {
        console.error('Error fetching stores:', e);
      }
      try {
        const res = await api.get('/users/riders/list', { params: { limit: 200 } });
        if (res.data?.success) {
          setRiders(res.data.riders || []);
        }
      } catch (e) {
        console.error('Error fetching riders:', e);
      }
    };
    init();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const buildParams = (format) => {
    const params = {
      from: filters.from,
      to: filters.to,
      includeReturns: filters.includeReturns ? 'true' : 'false',
      sequence: filters.sequence,
      format,
    };
    if (filters.storeId !== 'all') params.storeId = filters.storeId;
    if (filters.riderIds.length > 0) params.riderId = filters.riderIds.join(',');
    return params;
  };

  const getRiderLabel = (rider) =>
    `${`${rider.f_name || ''} ${rider.l_name || ''}`.trim() || rider.userId}${
      rider.mobileNo ? ` (${rider.mobileNo})` : ''
    }`;

  const renderSelectedRiders = (selectedIds) => {
    if (selectedIds.length === 0) return 'All Riders';
    if (selectedIds.length === 1) {
      const rider = riders.find((r) => r.userId === selectedIds[0]);
      return rider ? getRiderLabel(rider) : selectedIds[0];
    }
    return `${selectedIds.length} riders selected`;
  };

  const isRangeValid = () => {
    if (!filters.from || !filters.to) return false;
    return new Date(filters.from) <= new Date(filters.to);
  };

  const generateReport = async () => {
    if (!isRangeValid()) {
      setError('Please select a valid date range (From must be on or before To).');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setReport(null);
      const res = await api.get('/reports/rider-route-distance', {
        params: buildParams('json'),
        timeout: REPORT_TIMEOUT_MS,
      });
      if (res.data?.success) {
        setReport(res.data);
        setActiveTab(0);
        if ((res.data.routeSummary || []).length === 0) {
          actions.showSnackbar('No routes found for the selected filters', 'info');
        }
      } else {
        setError(res.data?.message || 'Failed to generate report');
      }
    } catch (e) {
      console.error('Error generating rider route distance report:', e);
      setError(e.response?.data?.message || 'Error generating report');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    if (!isRangeValid()) {
      setError('Please select a valid date range (From must be on or before To).');
      return;
    }
    try {
      setDownloading(true);
      setError('');
      const res = await api.get('/reports/rider-route-distance', {
        params: buildParams('xlsx'),
        responseType: 'blob',
        timeout: REPORT_TIMEOUT_MS,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `rider_route_distance_${filters.from}_to_${filters.to}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      actions.showSnackbar('Report downloaded successfully!', 'success');
    } catch (e) {
      console.error('Error downloading rider route distance report:', e);
      setError('Error downloading report');
      actions.showSnackbar('Error downloading report', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const totals = report?.totals;

  const summaryCards = [
    { label: 'Riders', value: totals?.riders ?? 0 },
    { label: 'Routes', value: totals?.routes ?? 0 },
    { label: 'Delivered Orders', value: totals?.ordersDelivered ?? 0 },
    { label: 'Total KM', value: totals?.totalKm ?? 0 },
  ];

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <RouteIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" component="h1">
            Rider Route Distance Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Road distance (Google Directions) per rider for travel allowance — store ▸ orders ▸ store.
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon sx={{ mr: 1 }} /> Filters
        </Typography>
        <Divider sx={{ my: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="From Date"
              type="date"
              value={filters.from}
              onChange={(e) => handleFilterChange('from', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: filters.to || todayStr() }}
              fullWidth
              InputProps={{ startAdornment: <DateIcon color="action" sx={{ mr: 1 }} /> }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="To Date"
              type="date"
              value={filters.to}
              onChange={(e) => handleFilterChange('to', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filters.from, max: todayStr() }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Store</InputLabel>
              <Select
                value={filters.storeId}
                onChange={(e) => handleFilterChange('storeId', e.target.value)}
                label="Store"
                startAdornment={<StoreIcon color="action" sx={{ mr: 1 }} />}
              >
                <MenuItem value="all">All Stores</MenuItem>
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.branchId || store.id}>
                    {store.storeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Riders</InputLabel>
              <Select
                multiple
                value={filters.riderIds}
                onChange={(e) => {
                  const value = e.target.value;
                  handleFilterChange(
                    'riderIds',
                    typeof value === 'string' ? value.split(',') : value
                  );
                }}
                input={<OutlinedInput label="Riders" />}
                renderValue={renderSelectedRiders}
              >
                {riders.map((rider) => (
                  <MenuItem key={rider.userId} value={rider.userId}>
                    <Checkbox checked={filters.riderIds.indexOf(rider.userId) > -1} />
                    <ListItemText primary={getRiderLabel(rider)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Sequence Mode</InputLabel>
              <Select
                value={filters.sequence}
                onChange={(e) => handleFilterChange('sequence', e.target.value)}
                label="Sequence Mode"
              >
                <MenuItem value="planned">Planned (route order)</MenuItem>
                <MenuItem value="actual">Actual (by delivered time)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.includeReturns}
                  onChange={(e) => handleFilterChange('includeReturns', e.target.checked)}
                />
              }
              label="Include Returned Orders"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={generateReport}
            disabled={loading || !isRangeValid()}
            startIcon={loading ? <CircularProgress size={20} /> : <GenerateIcon />}
            sx={{ minWidth: 200 }}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={downloadReport}
            disabled={downloading || !isRangeValid()}
            startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            sx={{ minWidth: 200 }}
          >
            {downloading ? 'Preparing...' : 'Download (Excel)'}
          </Button>
        </Box>
      </Paper>

      {report && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {summaryCards.map((card) => (
              <Grid item xs={6} md={3} key={card.label}>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="h4" color="primary.main">
                      {card.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {report.warnings && report.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Coverage Warnings ({report.warnings.length})</AlertTitle>
              <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
                {report.warnings.map((w, i) => (
                  <Typography key={i} variant="body2">
                    • {w}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}

          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label={`Rider Rollup (${report.riderRollup.length})`} />
              <Tab label={`Route Summary (${report.routeSummary.length})`} />
              <Tab label={`Leg Detail (${report.legDetail.length})`} />
            </Tabs>

            {activeTab === 0 && (
              <TableContainer sx={{ maxHeight: 560 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rider</TableCell>
                      <TableCell>Rider Id</TableCell>
                      <TableCell align="right">Routes</TableCell>
                      <TableCell align="right">Delivered</TableCell>
                      <TableCell align="right">Returned</TableCell>
                      <TableCell align="right">Total KM</TableCell>
                      <TableCell align="right">KM / Order</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.riderRollup.map((r) => (
                      <TableRow key={r.riderId} hover>
                        <TableCell>{r.riderName}</TableCell>
                        <TableCell>{r.riderId}</TableCell>
                        <TableCell align="right">{r.routes}</TableCell>
                        <TableCell align="right">{r.ordersDelivered}</TableCell>
                        <TableCell align="right">{r.ordersReturned}</TableCell>
                        <TableCell align="right">
                          <strong>{r.totalKm}</strong>
                        </TableCell>
                        <TableCell align="right">{r.kmPerOrder}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {activeTab === 1 && (
              <TableContainer sx={{ maxHeight: 560 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Rider</TableCell>
                      <TableCell>Store</TableCell>
                      <TableCell>Route Id</TableCell>
                      <TableCell align="right">Deliv.</TableCell>
                      <TableCell align="right">Ret.</TableCell>
                      <TableCell align="right">Stops</TableCell>
                      <TableCell>Leg Breakdown</TableCell>
                      <TableCell align="right">Total KM</TableCell>
                      <TableCell align="right">Dur (min)</TableCell>
                      <TableCell>Coverage</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.routeSummary.map((r, i) => (
                      <TableRow key={`${r.routeId}-${i}`} hover>
                        <TableCell>{r.date}</TableCell>
                        <TableCell>{r.riderName}</TableCell>
                        <TableCell>{r.storeName || r.storeId}</TableCell>
                        <TableCell>{r.routeId}</TableCell>
                        <TableCell align="right">{r.ordersDelivered}</TableCell>
                        <TableCell align="right">{r.ordersReturned}</TableCell>
                        <TableCell align="right">{r.stopsCounted}</TableCell>
                        <TableCell sx={{ maxWidth: 280, fontSize: '0.75rem', whiteSpace: 'normal' }}>
                          {r.legBreakdown}
                        </TableCell>
                        <TableCell align="right">
                          <strong>{r.totalRouteKm}</strong>
                        </TableCell>
                        <TableCell align="right">{r.totalDurationMin}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem' }}>{r.coverage}</TableCell>
                        <TableCell>
                          <Chip label={r.routeStatus} size="small" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {activeTab === 2 && (
              <TableContainer sx={{ maxHeight: 560 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Rider</TableCell>
                      <TableCell>Route Id</TableCell>
                      <TableCell align="right">Leg</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>To Status</TableCell>
                      <TableCell align="right">Distance KM</TableCell>
                      <TableCell align="right">Dur (min)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.legDetail.map((r, i) => (
                      <TableRow key={`${r.routeId}-${r.legNo}-${i}`} hover>
                        <TableCell>{r.date}</TableCell>
                        <TableCell>{r.riderName}</TableCell>
                        <TableCell>{r.routeId}</TableCell>
                        <TableCell align="right">{r.legNo}</TableCell>
                        <TableCell>{r.fromId}</TableCell>
                        <TableCell>{r.toId}</TableCell>
                        <TableCell>{r.toStatus}</TableCell>
                        <TableCell align="right">{r.distanceKm}</TableCell>
                        <TableCell align="right">{r.durationMin}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default RiderRouteDistanceReport;
